import * as React from "react";
import axios from "axios";

import { EnquiryContext } from "./context/EnquiryState";
import { DateTime } from "luxon";

const activeDayButton = 'button rounded-md text-center text-black font-semibold border-skin-site-menu-active border-2';
const inactiveDayButton = 'button rounded-md text-center border-skin-cacti border-2 text-black font-semibold';

const dayHasAvailabilitiesButton = 'button rounded-md text-center border-2 text-black font-semibold';
const dayHasNoAvailabilitiesButton = 'button rounded-md text-center border-grey border-2 text-gray-300 font-semibold';

function LiveTourBookingComponent() {

	type AvailabilityDetail = {
		startTime: string,
		endTime: string,
		sessionLength: number,
		isAvailable: boolean,
	}

	const {
		state,
		dispatch,
		TrackAnalyticsEvent
	} = React.useContext(EnquiryContext)

	// We can only fetch up to 7 days worth of availabilities in one go
	const BOOKING_WINDOW: number = 7

	const [isFetching, setIsFetching] = React.useState(true)
	const [availabilityDetails, setAvailabilityDetails] = React.useState([])
	const [selectedDate, setSelectedDate] = React.useState(null)
	const [dateRangeStartDate, setDateRangeStartDate] = React.useState(null)
	const [availibilitiesCache, setAvailibilitiesCache] = React.useState({})

	React.useEffect(() => {
		dispatch({ type: "SET_FORM_COMPONENT", payload: "liveTourBooking" })

		let startDate: DateTime

		if (state.form.liveTourBookingSlot != null) {
			startDate = state.form.liveTourBookingSlot

			setSelectedDate(startDate)
		} else {
			startDate = DateTime.now()
		}

		setDateRangeStartDate(startDate)

		getAvailabilities(availibilitiesCache, state.model.token, startDate, BOOKING_WINDOW)

		TrackAnalyticsEvent("live-tour-booking")
	}, [])

	const getAvailabilities = (availibilitiesCache: object, token: string, startDate: DateTime, bookingWindow: number) => {
		let endDate = startDate.plus({ days: bookingWindow })
		let now = DateTime.now()
		let cacheKey = `${startDate.toFormat("yyyy-MM-dd")}_${endDate.toFormat("yyyy-MM-dd")}`

		if (cacheKey in availibilitiesCache) {
			let cacheResult = availibilitiesCache[cacheKey]
			if (cacheResult != null && cacheResult.expiry > now) {
				setAvailabilityDetails(cacheResult.availabilityDetails)
				setIsFetching(false)

				return
			}

			availibilitiesCache[cacheKey] = null
		}

		let availabilityApiUrl = `/enquiry/getlivetouravailabilities`
		let data = {
			token: token,
			startDate: startDate.toFormat("yyyy-MM-dd"),
			endDate: endDate.toFormat("yyyy-MM-dd")
		}
		let headers = {
			'Content-Type': 'application/json',
		}

		const json = JSON.stringify(data)
		axios
			.post(availabilityApiUrl, json, { headers: headers })
			.then((response: any) => {
				let result = response.data

				//console.log(result)

				if (!result.isSuccess) {
					setIsFetching(false)

					dispatch({
						type: 'SET_LIVE_TOUR_BOOKING_ERROR',
						payload: result.errors[0]
					})

					return;
				}

				if (result.IsInvalidToken) {
					dispatch({
						type: 'SET_LIVE_TOUR_BOOKING_SESSION_EXPIRED',
						payload: true
					})

					setIsFetching(false)

					return;
				}

				if (result.isInvalidToken) {
					setIsFetching(false)

					console.error("Invalid token")
					return;
				}

				availibilitiesCache[cacheKey] = {
					expiry: now.plus({ minutes: 5 }),
					availabilityDetails: result.availabilityDetails
				}

				setAvailibilitiesCache(availibilitiesCache)
				setAvailabilityDetails(result.availabilityDetails)

				setIsFetching(false)

			}).catch(error => {
				console.log(error)

				dispatch({
					type: 'SET_LIVE_TOUR_BOOKING_ERROR',
					payload: 'Something went wrong please try again'
				})

				setIsFetching(false)
			})
	}

	const onSlotClick = (slotDateTime: DateTime, duration: number) => {
		//console.log("Slot time selected:", slotDateTime)

		let form = state.form
		form.liveTourBookingSlot = slotDateTime
		form.liveTourBookingDuration = duration

		dispatch({ type: "UPDATE_FORM", payload: form })
		dispatch({ type: "SET_LIVE_TOUR_BOOKING_ERROR", payload: null })
	}

	const onDateClick = (date: DateTime) => {
		setSelectedDate(date)

		let form = state.form
		form.liveTourBookingSlot = null
		form.liveTourBookingDuration = null

		dispatch({ type: "UPDATE_FORM", payload: form })
	}

	const fetchAvailabilities = (availibilitiesCache: object, daysToFetch: number, dateRangeStartDate: DateTime) => {
		let startDate: DateTime

		if (daysToFetch > 0) {
			startDate = dateRangeStartDate.plus({ day: BOOKING_WINDOW })

		} else {
			let now = DateTime.now()
			startDate = dateRangeStartDate
			if (now >= startDate) {
				return
			}

			let dateInPast = startDate.minus({ day: BOOKING_WINDOW })
			if (dateInPast >= now) {
				startDate = dateInPast
			} else {
				startDate = now
			}
		}

		setDateRangeStartDate(startDate)
		setIsFetching(true)
		getAvailabilities(availibilitiesCache, state.model.token, startDate, BOOKING_WINDOW)
	}

	function buildDateRange(startDate: DateTime) {
		let dateRange: DateTime[] = []
		for (var i = 0; i < BOOKING_WINDOW; i++) {
			dateRange.push(startDate.plus({ days: i }));
		}
		return dateRange
	}

	function buildAvailableDates(availibilitiesCache: object, dateRangeStartDate: DateTime, selectedDate: DateTime, slotDetails: AvailabilityDetail[], selectedSlot: DateTime) {
		let today = DateTime.now()
		let dates = buildDateRange(dateRangeStartDate)

		return (
			<>
				<h3 className="font-bold my-3">Date</h3>
				<div className="flex gap-3 mb-2 w-full justify-center items-center">
					{today < dates[0] &&
						<button className="flex justify-center items-center w-8 pb-3" onClick={(e) => fetchAvailabilities(availibilitiesCache, -7, dateRangeStartDate)}>
							<svg width="13" height="24" viewBox="0 0 13 24" fill="none" xmlns="http://www.w3.org/2000/svg">
								<path d="M12.25 23.25L1.53 12.53C1.46033 12.4604 1.40507 12.3778 1.36736 12.2869C1.32965 12.1959 1.31024 12.0984 1.31024 12C1.31024 11.9016 1.32965 11.8041 1.36736 11.7131C1.40507 11.6222 1.46033 11.5396 1.53 11.47L12.25 0.75" stroke="black" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
							</svg>
						</button>
					}
					<div id="date-viewport" className="flex gap-1 overflow-y-hidden overflow-x-auto items-center pb-3">
						{dates.map((date: DateTime) => {
							let isTodaySelected = selectedDate != null && date.toFormat("dd-MM-yyyy") == selectedDate.toFormat("dd-MM-yyyy");
							let hasAvailabilities = slotDetails.filter((slotDetail: AvailabilityDetail) => {
								let startTime: DateTime = DateTime.fromISO(slotDetail.startTime)

								return startTime.toFormat("dd-MM-yyyy") == date.toFormat("dd-MM-yyyy")
							}).length > 0

							return (
								<button key={date.toMillis()} className={`text-center`} onClick={(e) => onDateClick(date)} disabled={!hasAvailabilities} >
									<span className={`w-16 h-16 md:w-14 md:h-14 block flex flex-col items-center justify-center ${hasAvailabilities ? dayHasAvailabilitiesButton : dayHasNoAvailabilitiesButton}  ${isTodaySelected ? "border-skin-site-menu-active" : (hasAvailabilities ? "border-skin-cacti" : '"')}`}>
										<span className="leading-tight text-xs md:text-2xs lg:text-xs">{date.toFormat("EEE")}</span>
										<span className="leading-tight">{date.toFormat("d")}</span>
										<span className="leading-tight text-xs md:text-2xs lg:text-xs">{date.toFormat("MMM")}</span>
									</span>
								</button>
							)
						})}
					</div>
					<button className="flex justify-center items-center w-8 pb-3" onClick={(e) => fetchAvailabilities(availibilitiesCache, 7, dateRangeStartDate)}>
						<svg width="14" height="24" viewBox="0 0 14 24" fill="none" xmlns="http://www.w3.org/2000/svg">
							<path d="M1.5 0.75L12.22 11.47C12.2897 11.5396 12.3449 11.6222 12.3826 11.7131C12.4203 11.8041 12.4398 11.9016 12.4398 12C12.4398 12.0984 12.4203 12.1959 12.3826 12.2869C12.3449 12.3778 12.2897 12.4604 12.22 12.53L1.5 23.25" stroke="black" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
						</svg>
					</button>
				</div>
				<div className="flex justify-center items-center gap-2 space-x-3 mb-7">
					<div className="flex justify-center items-center gap-2">
						<div className="w-4 h-4 rounded-sm border-skin-cacti border-2"></div>
						<p className="text-xs">Available dates</p>
					</div>
					<div className="flex justify-center items-center gap-2">
						<div className="w-4 h-4 rounded-sm border-skin-site-menu-active border-2"></div>
						<p className="text-xs">Booking date selected</p>
					</div>
				</div>
			</>
		)
	}

	function buildAvailabileSlotSelection(dangeRangeStartDate: DateTime, slotDetails: AvailabilityDetail[], selectedDate: DateTime, selectedSlot: DateTime) {
		let dates: DateTime[] = buildDateRange(dangeRangeStartDate)

		if (selectedDate == null) {
			return
		}

		return (
			<>
				{selectedDate != null && dates.map((currentDate: DateTime) => {
					let availableSlots = slotDetails.filter((slotDetail: AvailabilityDetail) => {
						let startTime: DateTime = DateTime.fromISO(slotDetail.startTime)

						return startTime.toFormat("dd-MM-yyyy") == currentDate.toFormat("dd-MM-yyyy")
					});

					return (
						<>
							<div className={`${currentDate.toFormat("d MMMM yyyy") == selectedDate.toFormat("d MMMM yyyy") ? "" : "hidden"}`}>
								<div className="mb-6 font-semibold text-xl">
									Which time would you like to book your live tour?
								</div>
								<h3 className="font-bold my-3">Time</h3>

								<div className={`grid grid-cols-3 md:grid-cols-4 2xl:grid-cols-5 justify-items-center items-center gap-3`}>
									{
										availableSlots.map((slotDetail: AvailabilityDetail) => {
											let slotDateTime = DateTime.fromISO(slotDetail.startTime)
											let isSlotSelected = selectedSlot != null && slotDateTime.toFormat("d MM yyyy hh:mm a") == selectedSlot.toFormat("d MM yyyy hh:mm a")

											return (
												<button className={`w-28 ${isSlotSelected ? activeDayButton : inactiveDayButton}`} onClick={(e) => onSlotClick(slotDateTime, slotDetail.sessionLength)}>
													{slotDateTime.toFormat("hh:mm a")}
												</button>
											)
										})
									}
								</div>
							</div>
						</>
					)
				})}
			</>
		);
	}

	function buildSelectedTimeSlotDetails(slotDetail: DateTime) {
		if (slotDetail == null) {
			return null;
		}

		return (
			<div className="my-5 bg-skin-site-header rounded-lg p-4">
				Your live tour booking is on <span className="font-bold">{slotDetail.toFormat("EEE, d MMM yyyy, h:mm a")}</span>
			</div>
		);
	}

	return (
		<>
			<div className="mb-6 font-semibold text-xl">
				Which date would you like to book your live tour?
			</div>

			{isFetching &&
				<div className="my-5 flex flex-col gap-5 items-center">
					<img src="/img/loading-pendulum.svg" alt="loading" className="w-16 h-16" />
					<p className="text-center">Fetching live tour booking availabilities...</p>
				</div>
			}

			{!isFetching &&
				<>
					{state.liveTourBookingError != null && state.liveTourBookingError != "" &&
						<div className="mb-2 text-xs text-red-600">{state.liveTourBookingError}</div>
					}

					{buildSelectedTimeSlotDetails(state.form.liveTourBookingSlot)}

					<div className="date-range my-3">
						{buildAvailableDates(availibilitiesCache, dateRangeStartDate, selectedDate, availabilityDetails, state.form.liveTourBookingSlot)}
						{buildAvailabileSlotSelection(dateRangeStartDate, availabilityDetails, selectedDate, state.form.liveTourBookingSlot)}
					</div>
				</>
			}
		</>
	);

}

export { LiveTourBookingComponent }